// TODO @consumer: Rewrite PhotoObfuscated and replace it in this component.
// TODO @consumer: Rewrite "DownloadPhotoButton" and probably convert to hook.

import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import PhotoObfuscated from '../../../../../../../components/global/obfuscation/PhotoObfuscated';
import Badge from '../../../../../../../components/global/Badge';
import { getFaceboxes } from './helpers';
import { IMAGE_BLUR_TYPES } from '../../../../../../../constants/image-blur-types';
import useHorizontalSwipe from '../../../../../../../services/general/useHorizontalSwipe';
import MatchedPhotoBackgroundSelectionActions from './MatchedPhotoBackgroundSelectionActions';
import { setMatchedPhotoBackground } from '../../../../../../../actions/matched-photos';
import { getPhotoOrientation } from '../PurchasingOptions/components/PrintProductsGroup/helpers';
import { ORIENTATION } from '../../../../../../../services/cards/constants';

const propTypes = {
  matchedPhoto: PropTypes.shape({
    id: PropTypes.string.isRequired,
    photo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      faceblocked_thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
          blur_type: PropTypes.oneOf(Object.values(IMAGE_BLUR_TYPES)),
        }),
      ),
      thumbnail_urls: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string,
          width: PropTypes.number,
          height: PropTypes.number,
          size_code: PropTypes.oneOf(Object.values(IMAGE_SIZE_CODE)),
          regions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
        }),
      ),
      price_album_folder_tag: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        digital_downloads_price: PropTypes.shape({
          value: PropTypes.number.isRequired,
        }).isRequired,
      }),
      attribution: PropTypes.shape({
        name: PropTypes.string,
        username: PropTypes.string,
        avatar_photo: PropTypes.shape({
          thumbnail_urls: PropTypes.array,
        }),
      }),
    }).isRequired,
  }).isRequired,
  isEntitled: PropTypes.bool.isRequired,
  isPictureDay: PropTypes.bool.isRequired,
  actions: PropTypes.node,
  isAllPhotos: PropTypes.bool,
  includeTagName: PropTypes.bool,
  linkTo: PropTypes.string,
  withWatermark: PropTypes.bool,
  printBackgrounds: PropTypes.array,
  className: PropTypes.string,
};

const MatchedPhoto = ({
  matchedPhoto,
  isEntitled,
  isPictureDay,
  actions,
  isAllPhotos,
  includeTagName = true,
  linkTo,
  withWatermark,
  printBackgrounds,
  className,
}) => {
  const dispatch = useDispatch();
  const selectedPrintBackgroundId = useSelector(
    ({ matchedPhotos }) => matchedPhotos?.printBackgrounds?.[matchedPhoto.id],
  );
  const setSelectedPrintBackgroundId = useCallback((...args) =>
    dispatch(setMatchedPhotoBackground(...args)),
  );
  useEffect(() => {
    if (!selectedPrintBackgroundId && printBackgrounds?.length > 0) {
      const photoOrientation = getPhotoOrientation(matchedPhoto?.photo);
      const orientationBasedBackground = printBackgrounds.find(background => {
        const backgroundOrientation =
          background?.asset_image?.width >= background?.asset_image?.height
            ? ORIENTATION.LANDSCAPE
            : ORIENTATION.PORTRAIT;

        return photoOrientation === backgroundOrientation;
      });

      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId:
          orientationBasedBackground?.id ?? printBackgrounds[0].id,
      });
    }
  }, [printBackgrounds]);

  const selectedPrintBackground = useMemo(
    () =>
      printBackgrounds?.find(
        background => background.id === selectedPrintBackgroundId,
      ),
    [printBackgrounds, selectedPrintBackgroundId],
  );
  const onPrevBackground = useCallback(() => {
    if (!printBackgrounds || printBackgrounds?.length === 0) {
      return;
    }

    const currentIndex = printBackgrounds.indexOf(selectedPrintBackground);
    if (currentIndex - 1 < 0) {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[printBackgrounds.length - 1].id,
      });
    } else {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[currentIndex - 1].id,
      });
    }
  }, [selectedPrintBackground, printBackgrounds]);

  const onNextBackground = useCallback(() => {
    if (!printBackgrounds || printBackgrounds?.length === 0) {
      return;
    }

    const currentIndex = printBackgrounds.indexOf(selectedPrintBackground);
    if (currentIndex + 1 === printBackgrounds.length) {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[0].id,
      });
    } else {
      setSelectedPrintBackgroundId({
        matchedPhotoId: matchedPhoto.id,
        printBackgroundId: printBackgrounds[currentIndex + 1].id,
      });
    }
  }, [selectedPrintBackground, printBackgrounds]);

  const swipeHandlers = useHorizontalSwipe({
    onSwipeRight: onPrevBackground,
    onSwipeLeft: onNextBackground,
  });

  const history = useHistory();

  const {
    id,
    photo,
    faceblocked_thumbnail_urls: matchedFaceblockedThumbnails,
  } = matchedPhoto;

  const tagName = matchedPhoto.price_album_folder_tag?.name;

  const foregroundThumbnails = useMemo(() => {
    if (!photo?.foreground) {
      return [];
    }

    if (
      !isEntitled &&
      photo?.foreground?.watermarked_thumbnail_urls?.length > 0
    ) {
      return photo?.foreground?.watermarked_thumbnail_urls;
    }

    if (photo?.foreground?.thumbnail_urls?.length > 0) {
      return photo?.foreground?.thumbnail_urls;
    }

    return [photo?.foreground?.original_url];
  }, [photo, isEntitled]);

  const faceblockedThumbnails = useMemo(() => {
    return matchedFaceblockedThumbnails?.length > 0
      ? matchedFaceblockedThumbnails
      : photo?.faceblocked_thumbnail_urls;
  }, [matchedPhoto]);

  const shouldUseForegroundUrls = useMemo(
    () => foregroundThumbnails?.length > 0,
    [photo],
  );

  const shouldUseFaceblockedUrls = useMemo(
    () =>
      !shouldUseForegroundUrls &&
      faceblockedThumbnails?.length > 0 &&
      !isEntitled &&
      isPictureDay,
    [photo, shouldUseForegroundUrls, isEntitled, isPictureDay],
  );

  const faceboxes = useMemo(() => {
    if (shouldUseForegroundUrls) {
      return [];
    }

    if (shouldUseFaceblockedUrls) {
      const foregroundThumb = faceblockedThumbnails.find(
        ({ blur_type: blurType }) => blurType === IMAGE_BLUR_TYPES.FOREGROUND,
      );

      if (foregroundThumb?.regions) {
        return foregroundThumb?.regions;
      }
    }

    return getFaceboxes(matchedPhoto, !isAllPhotos);
  }, [matchedPhoto, isAllPhotos, isEntitled, isPictureDay]);

  const thumbs = useMemo(() => {
    if (shouldUseFaceblockedUrls) {
      return faceblockedThumbnails;
    }

    if (foregroundThumbnails.length > 0) {
      return foregroundThumbnails;
    }

    return withWatermark
      ? photo.watermarked_urls ?? photo.thumbnail_urls
      : photo.thumbnail_urls;
  }, [photo, withWatermark, isEntitled, isPictureDay]);

  const obfuscatorMultiplier = useMemo(() => {
    if (shouldUseForegroundUrls) {
      return 0;
    }

    if (shouldUseFaceblockedUrls) {
      return 1;
    }

    return isEntitled || isAllPhotos ? 0 : 1.8;
  }, [isEntitled, isPictureDay, isAllPhotos]);

  const maxThumbSize = useMemo(() => {
    if (shouldUseFaceblockedUrls) {
      return IMAGE_SIZE_CODE.LARGE;
    }

    // On FWG, non-entitled items must show on medium res.
    return !isEntitled && isAllPhotos
      ? IMAGE_SIZE_CODE.MEDIUM2X
      : IMAGE_SIZE_CODE.LARGE;
  }, [isEntitled, isPictureDay, isAllPhotos]);

  const printBackgroundStyles = useMemo(() => {
    if (
      foregroundThumbnails.length === 0 ||
      !selectedPrintBackground?.asset_image
    ) {
      return {};
    }

    return {
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundImage: `url(${selectedPrintBackground.asset_image.url})`,
    };
  }, [selectedPrintBackground]);

  return (
    <div
      {...swipeHandlers}
      className={classNames({
        'matched-grid-photo': true,
        'has-bottom-elements': includeTagName && tagName,
        [className]: !!className,
      })}
      style={{
        ...printBackgroundStyles,
      }}
    >
      <PhotoObfuscated
        id={id}
        thumbs={thumbs}
        faceboxes={faceboxes}
        shouldUseFaceblockedUrls={shouldUseFaceblockedUrls}
        obfuscatorMultiplier={obfuscatorMultiplier}
        blockerMinSizeThreshold={0.005}
        blockerMinSize={0.3}
        blockerMaxSize={0.5}
        minThumbnailSize={IMAGE_SIZE_CODE.MEDIUM2X}
        maxThumbnailSize={maxThumbSize}
        onClick={
          (isEntitled || isAllPhotos) && linkTo
            ? () => history.push(linkTo)
            : undefined
        }
      />

      {actions && <div>{actions}</div>}

      {includeTagName && tagName && (
        <Badge
          className="matched-grid-photo-tag"
          badgeStyle="dark-transparent-light"
        >
          {tagName}
        </Badge>
      )}
      {printBackgrounds?.length > 0 && foregroundThumbnails?.length > 0 && (
        <MatchedPhotoBackgroundSelectionActions
          printBackgrounds={printBackgrounds}
          foregroundThumbnais={foregroundThumbnails}
          onPrevBackground={onPrevBackground}
          onNextBackground={onNextBackground}
        />
      )}
    </div>
  );
};

MatchedPhoto.propTypes = propTypes;

export default MatchedPhoto;
